// MERCADO: cotacao, interesse do atleta, negociacao e IA compradora.
//
// A mecanica e a mesma do 38 Brasil, reescalada para basquete:
//
//   preco pedido = valor de mercado x 1,08 x multiplicador de interesse
//
// O atleta nao e uma etiqueta de preco: ele avalia quem esta comprando. Clube
// grande atrai, clube pequeno espanta, salario alto compensa, e um astro nao
// desce de nivel so porque o dinheiro apareceu. Interesse baixo NAO trava a
// negociacao -- encarece (ate 1,28x), que e o comportamento do jogo antigo.
//
// O que o basquete acrescenta: a folha salarial. Nao adianta ter caixa se o
// salario nao cabe no teto, e o elenco tem piso de 12 e teto de 15 atletas.

import { makeRng, clamp } from './rng.js';
import { MONEY, marketValueOf, wageOf, payrollOf } from './economy.js';

// ---------------------------------------------------------------- interesse

/** Prestigio do clube: 0 (fundo da liga) a 10 (candidato ao titulo). */
export function clubPrestige(team, standings) {
  const record = standings?.[team.id];
  const winPct = record && record.games > 0 ? record.wins / record.games : 0.5;
  // Metade e a forca do elenco, metade e a campanha atual.
  const squad = team.roster.length
    ? team.roster.reduce((a, p) => a + p.overall, 0) / team.roster.length
    : 70;
  return clamp((squad - 66) * 0.55 + (winPct - 0.5) * 8 + 5, 0, 10);
}

/**
 * Interesse do atleta em vestir a camisa do comprador. 0..100.
 * Espelha `managerPlayerTransferInterest38`: prestigio, papel esperado,
 * salario oferecido, idade e o "clube dos sonhos".
 */
export function playerInterest({ player, buyer, seller, offeredWage, standings }) {
  let score = 48;

  const buyerPrestige = clubPrestige(buyer, standings);
  const sellerPrestige = seller ? clubPrestige(seller, standings) : 5;

  // Subir de patamar atrai; descer espanta.
  score += (buyerPrestige - sellerPrestige) * 5.2;

  // Papel esperado: quantos do elenco comprador sao melhores que ele?
  const betterThanHim = buyer.roster.filter((p) => p.overall > player.overall).length;
  if (betterThanHim === 0) score += 14;          // chega como o melhor
  else if (betterThanHim <= 2) score += 7;       // titular certo
  else if (betterThanHim >= 8) score -= 16;      // vai para o fim do banco
  else if (betterThanHim >= 5) score -= 7;

  // Salario oferecido contra o que ele ja ganha.
  const currentWage = player.wage || wageOf(player);
  const wageRatio = (offeredWage || currentWage) / Math.max(0.1, currentWage);
  score += (wageRatio - 1) * 34;

  // Astro nao desce de nivel; jovem aceita mais facil.
  if (player.overall >= 86 && buyerPrestige < sellerPrestige) score -= 15;
  if (player.age <= 23) score += 6;
  if (player.age >= 33) score += 4; // veterano quer jogar onde pagam

  // Clube dos sonhos: constante por atleta, para o mesmo jogador responder
  // sempre igual ao mesmo clube.
  const dream = makeRng(`sonho|${player.id}|${buyer.id}`)();
  score += (dream - 0.5) * 12;

  return Math.round(clamp(score, 0, 100));
}

export function interestLabel(score) {
  if (score >= 72) return 'quer muito';
  if (score >= 58) return 'interessado';
  if (score >= 45) return 'aberto a ouvir';
  if (score >= 32) return 'reticente';
  if (score >= 22) return 'so por muito dinheiro';
  return 'nao quer sair';
}

/** Interesse baixo encarece o negocio -- nunca o impede. */
export function interestAskMultiplier(score) {
  if (score >= 45) return 1;
  if (score >= 32) return 1.12;
  if (score >= 22) return 1.2;
  return 1.28;
}

// ------------------------------------------------------------------ cotacao

/**
 * Cotacao completa de uma compra: o que o vendedor pede, o que o atleta quer
 * ganhar, e se cabe no elenco e na folha do comprador.
 */
export function quote({ player, buyer, seller, standings, offeredWage, econ = MONEY }) {
  const value = player.marketValue ?? marketValueOf(player);
  const wageWanted = Math.round(wageOf(player) * 1.06 * 10) / 10; // wageOf ja devolve o gravado
  const wage = offeredWage ?? wageWanted;
  const interest = playerInterest({ player, buyer, seller, offeredWage: wage, standings });
  const mult = interestAskMultiplier(interest);
  const ask = Math.round(value * MONEY.askMultiplier * mult * 10) / 10;

  const payroll = payrollOf(buyer.roster);
  const isStar = player.overall >= 86;

  return {
    value,
    ask,
    wageWanted,
    interest,
    interestText: interestLabel(interest),
    fair: Math.round(value * MONEY.fairMultiplier * 10) / 10,
    stretch: Math.round(value * (isStar ? MONEY.starStretch : MONEY.stretchMultiplier) * 10) / 10,
    payrollAfter: Math.round((payroll + wage) * 10) / 10,
    capSpace: Math.round((econ.salaryCap - payroll - wage) * 10) / 10,
    rosterAfter: buyer.roster.length + 1,
    isStar,
  };
}

/** Motivos que impedem a compra antes mesmo de negociar. */
export function blockers({ player, buyer, fee, wage, quote: q, econ = MONEY }) {
  const out = [];
  if (buyer.roster.length >= econ.maxRoster) {
    out.push(`Elenco cheio: ${econ.maxRoster} atletas. Venda ou dispense alguem antes.`);
  }
  if (fee > buyer.money) {
    out.push(`Caixa insuficiente: voce tem ${buyer.money.toFixed(1)} e a proposta e ${fee.toFixed(1)}.`);
  }
  if (q.payrollAfter > econ.salaryCap * 1.25) {
    out.push(`A folha iria a ${q.payrollAfter.toFixed(1)}, muito acima do teto de ${econ.salaryCap}.`);
  }
  void wage;
  return out;
}

// -------------------------------------------------------------- negociacao

/**
 * O clube vendedor avalia a proposta do usuario.
 * Espelha `managerEvaluateBuyerCounterOffer38`: compara com o justo, com o
 * esticado, e devolve contraproposta quando esta perto.
 */
export function evaluateBid({ player, fee, wage, q }) {
  const wageOk = wage >= q.wageWanted * 0.94;

  if (!wageOk) {
    return {
      status: 'wage',
      counterWage: q.wageWanted,
      message: `${player.name} quer ${q.wageWanted.toFixed(1)} mi por temporada. Sua oferta salarial ficou abaixo.`,
    };
  }

  if (fee >= q.ask) {
    return { status: 'accepted', message: `Negocio fechado por ${fee.toFixed(1)} mi.` };
  }

  // Perto do pedido: o vendedor volta com um numero.
  if (fee >= q.ask * 0.82) {
    const counter = Math.round(((q.ask + fee) / 2) * 10) / 10;
    return {
      status: 'counter',
      counterFee: counter,
      message: `Contraproposta: ${counter.toFixed(1)} mi e fechamos.`,
    };
  }

  if (fee >= q.ask * 0.62) {
    return {
      status: 'rejected',
      message: `Recusado. O clube pede ${q.ask.toFixed(1)} mi e nao chega perto disso.`,
    };
  }

  return {
    status: 'insulted',
    message: `Proposta recusada na hora. ${q.ask.toFixed(1)} mi e o preco, e eles nem quiseram conversar.`,
  };
}

// ------------------------------------------------------------ IA compradora

/** Teto que um clube da IA estica por um atleta. */
export function aiMaxFee(buyer, player) {
  const value = player.marketValue ?? marketValueOf(player);
  const isStar = player.overall >= 86;
  const stretch = value * (isStar ? MONEY.starStretch : MONEY.stretchMultiplier);
  // Nunca so o caixa: um clube nao gasta tudo que tem num atleta so.
  const budget = buyer.money * 0.62;
  return Math.round(Math.min(stretch, budget) * 10) / 10;
}

/** A IA quer este atleta? Nao adianta comprar quem nao melhora o time. */
export function aiWants(buyer, player, econ = MONEY) {
  if (buyer.roster.length >= econ.maxRoster) return false;
  const payroll = payrollOf(buyer.roster);
  if (payroll + (player.wage || wageOf(player)) > econ.salaryCap * 1.1) return false;

  const sorted = [...buyer.roster].sort((a, b) => b.overall - a.overall);
  const eighth = sorted[7]?.overall ?? 60;
  // Melhora a rotacao de verdade, ou e jovem com teto alto.
  return player.overall > eighth + 1
    || (player.age <= 23 && (player.potential ?? player.overall) > eighth + 4);
}

/**
 * O quanto o mercado deseja este atleta, de 0 a 1.
 *
 * Antes a chance de alguem procurar um atleta seu era FIXA: 1,2% por rodada
 * para quem nao esta listado. Medido em 04/09/2026, isso dava 4 propostas em
 * 30 rodadas -- o mercado parecia morto porque quase era.
 *
 * Agora a chance sai de quatro coisas que um diretor de verdade olha:
 *
 *   quem e o atleta   um pivo de 88 e assediado; o decimo segundo homem, nao
 *   esta a venda?     listar e avisar o mercado, e o efeito e enorme
 *   esta encostado?   quem e bom e nao joga vira alvo -- todo mundo percebe
 *   o relogio         perto da data limite o assedio dispara, como na vida real
 */
function cobicaDe(player, { listado, encostado, aperto }) {
  // A base cresce rapido com o overall: 70 quase nao interessa, 90 interessa
  // a todo mundo. O expoente e o que separa "elenco" de "alvo".
  const nivel = clamp((player.overall - 68) / 22, 0, 1) ** 1.6;
  let c = 0.02 + nivel * 0.10;

  // Jovem com teto alto tambem atrai, mesmo sem overall de hoje.
  const teto = player.potential ?? player.overall;
  if (player.age <= 23 && teto >= player.overall + 5) c += 0.03;

  if (listado) c *= 4.2;        // listar e avisar o mercado
  if (encostado) c *= 1.9;      // bom e sem jogar: alvo obvio
  c *= 1 + aperto * 1.2;        // a data limite empurra todo mundo

  return clamp(c, 0, 0.55);
}

/**
 * Gera as propostas que chegam ao usuario.
 *
 * Duas mudancas em relacao a versao anterior, alem do volume: o comprador nao
 * e mais sorteado entre os interessados (quem PRECISA mais paga mais, e e quem
 * costuma aparecer), e a proposta carrega o motivo -- a tela mostra por que
 * aquele clube quer aquele atleta.
 */
export function generateAiOffers({
  league, userTeam, listedIds, standings, round, season, rng, econ = MONEY,
  deadlineRound = 0,
}) {
  const offers = [];
  const listed = new Set(listedIds);

  // 0 no comeco da temporada, 1 na vespera da data limite.
  const aperto = deadlineRound > 0
    ? clamp((round / deadlineRound) ** 2, 0, 1)
    : 0;

  // "Encostado" e quem esta fora da rotacao de 9 do PROPRIO time do usuario.
  const meus = [...userTeam.roster].sort((a, b) => b.overall - a.overall);
  const encostados = new Set(meus.slice(9).map((p) => p.id));

  for (const player of userTeam.roster) {
    const chance = cobicaDe(player, {
      listado: listed.has(player.id),
      encostado: encostados.has(player.id),
      aperto,
    });
    if (!rng.chance(chance)) continue;

    const candidates = league.teams.filter((t) => t.id !== userTeam.id && aiWants(t, player, econ));
    if (!candidates.length) continue;

    /* Quem mais precisa e quem aparece. Antes era sorteio puro, e um clube
       cheio de opcoes na posicao aparecia tanto quanto o desesperado. */
    const comNecessidade = candidates.map((t) => {
      const sorted = [...t.roster].sort((a, b) => b.overall - a.overall);
      const oitavo = sorted[7]?.overall ?? 60;
      return { time: t, ganho: player.overall - oitavo, caixa: t.money };
    }).sort((a, b) => (b.ganho * 2 + b.caixa / 20) - (a.ganho * 2 + a.caixa / 20));

    // Entre os tres mais interessados, para nao ser sempre o mesmo clube.
    const top = comNecessidade.slice(0, 3);
    const escolhido = top[rng.int(top.length)];
    const buyer = escolhido.time;

    const max = aiMaxFee(buyer, player);
    const value = player.marketValue ?? marketValueOf(player);
    if (max < value * 0.7) continue;

    /* Perto da data limite paga-se mais caro -- e a mesma pressa que fez a
       proposta existir. */
    const pressa = 1 + aperto * 0.12;
    const fee = Math.round(clamp(max * rng.range(0.78, 1.0) * pressa, value * 0.6, max) * 10) / 10;
    const wage = Math.round(wageOf(player) * rng.range(1.0, 1.18) * 10) / 10;

    offers.push({
      id: `of-${season}-${round}-${player.id}-${buyer.id}`,
      kind: 'buy',
      playerId: player.id,
      playerName: player.name,
      buyerId: buyer.id,
      buyerName: buyer.name,
      fee,
      wage,
      round,
      // 5 e nao 3: com 3, quem avanca de 5 em 5 rodadas via a proposta nascer e
      // morrer sem nunca ter tido a chance de responder.
      expiresRound: round + 5,
      listed: listed.has(player.id),
      // Aparece na tela: da para decidir sabendo o que o outro lado quer.
      motivo: escolhido.ganho >= 6 ? 'quer titular'
        : escolhido.ganho >= 2 ? 'reforca a rotacao'
          : 'apostando no futuro',
    });
  }
  void standings;
  return offers;
}

/** O comprador da IA aceita a contraproposta do usuario? */
export function aiAcceptsCounter({ buyer, player, originalFee, counterFee }) {
  const max = aiMaxFee(buyer, player);
  const hike = (counterFee - originalFee) / Math.max(0.1, originalFee);
  if (counterFee > max) {
    return { ok: false, message: `${buyer.name} nao chega a ${counterFee.toFixed(1)} mi. O teto deles e ${max.toFixed(1)}.` };
  }
  if (hike > 0.45) {
    return { ok: false, message: `${buyer.name} achou o aumento grande demais e retirou a proposta.` };
  }
  return { ok: true, message: `${buyer.name} aceitou ${counterFee.toFixed(1)} mi.` };
}

/** Quanto a IA paga para tirar um atleta do elenco por dispensa (rescisao). */
export function releaseCost(player) {
  // Rescindir custa o salario restante do contrato, com desconto.
  const years = Math.max(1, player.contractYears || 1);
  return Math.round((player.wage || wageOf(player)) * years * 0.55 * 10) / 10;
}
